/**
 * src/shell/hooks/useMobileLayout.ts
 *
 * Adaptive Mobile Layout Hook.
 * Tracks viewport breakpoints, orientation, keyboard visibility,
 * and safe-area inset dimensions.
 */

import { useState, useEffect, useCallback } from 'react';

export type Breakpoint = 'xs' | 'sm' | 'md' | 'lg' | 'xl';
export type Orientation = 'portrait' | 'landscape';

export interface SafeAreaInsets {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

export interface MobileLayoutState {
  /** Viewport width in px */
  viewportWidth: number;
  /** Viewport height in px */
  viewportHeight: number;
  /** Named breakpoint */
  breakpoint: Breakpoint;
  /** True when width < 768px */
  isMobile: boolean;
  /** True when 768px <= width < 1024px */
  isTablet: boolean;
  /** True when width >= 1024px */
  isDesktop: boolean;
  /** Screen orientation */
  orientation: Orientation;
  /** True if virtual keyboard is likely visible (height shrank significantly) */
  hasVirtualKeyboard: boolean;
  /** CSS env() safe-area inset values in px */
  safeArea: SafeAreaInsets;
}

function getBreakpoint(width: number): Breakpoint {
  if (width < 480) return 'xs';
  if (width < 768) return 'sm';
  if (width < 1024) return 'md';
  if (width < 1280) return 'lg';
  return 'xl';
}

function readSafeAreaInsets(): SafeAreaInsets {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return { top: 0, bottom: 0, left: 0, right: 0 };
  }
  // Read computed CSS env() values via a temporary probe element
  const probe = document.createElement('div');
  probe.style.cssText = [
    'position:fixed',
    'top:env(safe-area-inset-top,0px)',
    'bottom:env(safe-area-inset-bottom,0px)',
    'left:env(safe-area-inset-left,0px)',
    'right:env(safe-area-inset-right,0px)',
    'pointer-events:none',
    'visibility:hidden',
  ].join(';');
  document.body.appendChild(probe);
  const cs = getComputedStyle(probe);
  const result: SafeAreaInsets = {
    top: parseFloat(cs.top) || 0,
    bottom: parseFloat(cs.bottom) || 0,
    left: parseFloat(cs.left) || 0,
    right: parseFloat(cs.right) || 0,
  };
  document.body.removeChild(probe);
  return result;
}

export function useMobileLayout(): MobileLayoutState {
  const getState = useCallback((): MobileLayoutState => {
    if (typeof window === 'undefined') {
      return {
        viewportWidth: 1280,
        viewportHeight: 800,
        breakpoint: 'lg',
        isMobile: false,
        isTablet: false,
        isDesktop: true,
        orientation: 'landscape',
        hasVirtualKeyboard: false,
        safeArea: { top: 0, bottom: 0, left: 0, right: 0 },
      };
    }

    const width = window.innerWidth;
    const height = window.innerHeight;
    const bp = getBreakpoint(width);

    return {
      viewportWidth: width,
      viewportHeight: height,
      breakpoint: bp,
      isMobile: width < 768,
      isTablet: width >= 768 && width < 1024,
      isDesktop: width >= 1024,
      orientation: width > height ? 'landscape' : 'portrait',
      // Heuristic: if window height is <60% of screen height, keyboard is likely visible
      hasVirtualKeyboard:
        typeof screen !== 'undefined' && screen.height > 0 && height / screen.height < 0.6,
      safeArea: readSafeAreaInsets(),
    };
  }, []);

  const [state, setState] = useState<MobileLayoutState>(getState);

  useEffect(() => {
    const update = () => setState(getState());
    window.addEventListener('resize', update, { passive: true });
    window.addEventListener('orientationchange', update, { passive: true });
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('orientationchange', update);
    };
  }, [getState]);

  return state;
}
